import { EllipsisIcon, MoreVertOutlined as MoreVertOutlinedIcon } from 'components/icons';
import { FC, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import StatusChip, { ChipType } from 'components/@extended/StatusChip';
import PermissionButton from 'components/buttons/PermissionButton';
import PinButton from 'components/buttons/PinButton';
import DmsLink from 'components/dmslink/DmsLink';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import KebabMenu, { MenuItem } from 'components/navigation/KebabMenu';
import Unmasked from 'components/Unmasked';
import PageHeader from 'components/PageHeader';
import { OUTLINED_ACTION_CLASS, PRIMARY_ACTION_CLASS } from 'components/record-header/RecordHeader';
import { Tooltip } from 'components/ui/tooltip';
import NMoreChip from 'components/util/NMoreChip';
import { useDeleteCorrespondenceMutation } from 'store/api/correspondence';
import { ClassifierData } from 'types/classifiers';
import { CorrespondenceDetailsDto, CorrespondenceStatusType } from 'types/correspondence';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { recordPin } from 'utils/pins';
import { useGoBack, useLeaveDeletedEntity, useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import CorrespondenceMatterItem from './CorrespondenceMatterItem';

interface Props {
    correspondence: CorrespondenceDetailsDto;
    classifiers: Record<string, ClassifierData>;
    /** The new design: no back arrow and no Edit, as each section edits itself; the record's title and actions. */
    isNew?: boolean;
}

const SECTION_CLASS = 'flex items-center gap-[.5ch] text-[12px] leading-5 font-medium';

/** Who may edit a correspondence: its Edit here in classic, its sections' Edit in the new design. */
export const CORRESPONDENCE_UPDATE_PERMISSIONS = [
    'UPDATE.CORRESPONDENCE.ALL',
    'UPDATE.CORRESPONDENCE.MY_CASES',
    'UPDATE.CORRESPONDENCE.CREATED_BY_ME',
    'UPDATE.CORRESPONDENCE.MY_OWN',
    'UPDATE.CORRESPONDENCE.MY_TEAM_CASES',
];

const CorrespondencePageHeader: FC<Props> = ({ correspondence, classifiers, isNew = false }) => {
    const navigateWithReturn = useNavigateWithReturnState();
    const goBack = useGoBack('/correspondence');
    const leaveDeleted = useLeaveDeletedEntity('/correspondence');
    const { openModal, closeModal } = useModal();
    const [deleteCorrespondence] = useDeleteCorrespondenceMutation();
    const hasDeletePermission = useHasPermissions(['DELETE.CORRESPONDENCE.ALL']);

    const statusChipType = useMemo<ChipType>(() => {
        switch (correspondence.statusTypeCode) {
            case CorrespondenceStatusType.IN_PROGRESS:
                return 'success';
            default:
                return 'neutral';
        }
    }, [correspondence.statusTypeCode]);

    const sortedMatters = useMemo(
        () =>
            Array.from(correspondence.matters || []).sort((a, b) =>
                a.fullName.toLocaleUpperCase() < b.fullName.toLocaleUpperCase() ? -1 : 0
            ),
        [correspondence.matters]
    );
    const firstMatter = sortedMatters.at(0);

    const deleteMatterHandler = useCallback(() => {
        if (correspondence.id && !isNaN(Number(correspondence.id))) {
            openModal(
                <ConfirmationModal
                    onCancel={closeModal}
                    onConfirm={() => {
                        void deleteCorrespondence({ id: `${correspondence.id}` })
                            .unwrap()
                            .then(leaveDeleted)
                            .catch(console.error)
                            .finally(() => closeModal());
                    }}
                    content={
                        <p className="text-body1">
                            <FormattedMessage id="correspondence.delete-confirmation-text" />
                        </p>
                    }
                />
            );
        }
    }, [deleteCorrespondence, closeModal, openModal, correspondence.id, leaveDeleted]);

    const menuItems = useMemo<MenuItem[]>(() => {
        const items: MenuItem[] = [];
        if (hasDeletePermission) {
            items.push({
                title: t('delete'),
                handler: deleteMatterHandler,
                variant: 'destructive',
            });
        }
        return items;
    }, [deleteMatterHandler, hasDeletePermission]);

    return (
        <PageHeader
            title={correspondence.number}
            titleNode={<Unmasked>{correspondence.number}</Unmasked>}
            showBreadcrumb
            onBack={isNew ? undefined : goBack}
            buttons={
                <div className={cn('flex justify-end gap-2', isNew && 'items-center')}>
                    {menuItems.length > 0 && (
                        <KebabMenu
                            menuItems={menuItems}
                            className={
                                isNew ? cn(OUTLINED_ACTION_CLASS, 'hover:text-foreground size-9 border') : 'h-10'
                            }
                            icon={
                                isNew ? <EllipsisIcon className="size-4" /> : <MoreVertOutlinedIcon fontSize="medium" />
                            }
                        />
                    )}
                    {isNew ? null : (
                        <PermissionButton
                            permissions={CORRESPONDENCE_UPDATE_PERMISSIONS}
                            variant="outlined"
                            size="small"
                            onClick={() => navigateWithReturn(`/correspondence/${correspondence.id}/edit`)}
                        >
                            <FormattedMessage id="edit" />
                        </PermissionButton>
                    )}
                    <PermissionButton
                        permissions={['CREATE.TASKS']}
                        variant="contained"
                        color="secondary"
                        size="small"
                        className={isNew ? PRIMARY_ACTION_CLASS : undefined}
                        onClick={() => navigateWithReturn(`/correspondence/${correspondence.id}/task/new`)}
                    >
                        <FormattedMessage id="correspondence.add-new-task" />
                    </PermissionButton>
                </div>
            }
        >
            <div
                className={cn(
                    'flex w-full flex-wrap items-center gap-4 py-4 max-sm:gap-2',
                    // The new design's header band carries its own bottom spacing.
                    isNew && 'pt-2 pb-0'
                )}
            >
                <div className={SECTION_CLASS}>
                    <StatusChip
                        type={statusChipType}
                        label={getClassifierText(
                            classifiers['CORRESPONDENCE_STATUS_TYPE'],
                            correspondence.statusTypeCode
                        )}
                    />
                    {isNew ? (
                        <PinButton pin={recordPin('correspondence', correspondence.id, correspondence.number)} />
                    ) : null}
                </div>
                {correspondence.dmsLink ? <DmsLink link={correspondence.dmsLink} isIconDisplayed /> : null}
                <div className={SECTION_CLASS}>
                    <FormattedMessage id="correspondence.connected-cases" />
                    :&nbsp;
                    {firstMatter ? <CorrespondenceMatterItem matter={firstMatter} linkClassName="text-link" /> : '-'}
                    {sortedMatters.length > 1 ? (
                        <div>
                            <Tooltip
                                content={
                                    <div className="flex flex-col">
                                        {sortedMatters.slice(1).map((matter) => (
                                            <CorrespondenceMatterItem
                                                key={matter.id}
                                                matter={matter}
                                                linkClassName="text-link"
                                            />
                                        ))}
                                    </div>
                                }
                            >
                                <NMoreChip count={sortedMatters.length - 1} />
                            </Tooltip>
                        </div>
                    ) : null}
                </div>
            </div>
        </PageHeader>
    );
};

export default CorrespondencePageHeader;
